import { 
  generateStepName, 
  generateInternalId, 
  createStepIdMapping, 
  resolveActionTarget 
} from './stepIdManagement';
import { VALID_STEP_TYPES, DEFAULT_STEP_CONFIGS } from './workflowConstants';
import { isStepComplete } from './stepValidation';

/**
 * Validates and processes imported workflow JSON
 * @param {Object} importedWorkflow - Raw imported workflow object
 * @returns {Object} - Processing result with workflow and warnings
 */
export const processWorkflowImport = (importedWorkflow) => {
  const warnings = [];
  
  // Validate required fields
  if (!importedWorkflow.name) {
    throw new Error('Imported workflow must have a name');
  }
  
  if (!importedWorkflow.steps || !Array.isArray(importedWorkflow.steps)) {
    throw new Error('Imported workflow must have a steps array');
  }
  
  // First pass: Create step mapping and validate step IDs
  const stepIdMap = new Map();
  const stepNameMap = new Map();
  
  // Process and validate steps
  const processedSteps = importedWorkflow.steps.map((stepData, index) => {
    // Validate step type
    if (!VALID_STEP_TYPES.includes(stepData.type)) {
      throw new Error(`Invalid step type "${stepData.type}" at step ${index + 1}. Must be one of: ${VALID_STEP_TYPES.join(', ')}`);
    }
    
    const stepName = stepData.name || `${stepData.type} Step ${index + 1}`;
    const internalId = generateInternalId('step');
    const yamlStepName = generateStepName(stepName, stepData.type);
    
    // Map various ways this step might be referenced
    if (stepData.id) {
      stepIdMap.set(stepData.id, internalId);
    }
    stepNameMap.set(stepName.toLowerCase(), internalId);
    stepNameMap.set(yamlStepName, internalId);
    
    // Common name variations for action routing
    const nameVariations = [
      stepName.toLowerCase().replace(/\s+/g, '_'),
      stepName.toLowerCase().replace(/\s+/g, ''),
      yamlStepName,
      `step_${index + 1}`,
      `step${index + 1}`,
      stepData.id || ''
    ];
    
    nameVariations.forEach(variation => {
      if (variation) {
        stepIdMap.set(variation, internalId);
      }
    });
    
    // Create processed step with default config merged
    const defaultConfig = DEFAULT_STEP_CONFIGS[stepData.type] || {};
    const processedStep = {
      id: internalId,
      originalId: stepData.id, // Keep original ID for reference
      stepName: yamlStepName,
      name: stepName,
      type: stepData.type,
      config: { ...defaultConfig, ...stepData.config },
      actionRouting: {},
      saved: false,
      isStart: index === 0, // First step is start by default
      isEnd: false
    };
    
    // Auto-mark as saved if step is complete
    if (isStepComplete(processedStep)) {
      processedStep.saved = true;
    }
    
    return processedStep;
  });
  
  // Second pass: Process action routing with validation
  processedSteps.forEach((step, stepIndex) => {
    const originalStepData = importedWorkflow.steps[stepIndex];
    const actionRouting = originalStepData.actionRouting || originalStepData.on || {};
    
    Object.entries(actionRouting).forEach(([action, target]) => {
      const resolvedTarget = resolveActionTarget(target, stepIdMap, stepNameMap);
      
      if (resolvedTarget) {
        step.actionRouting[action] = resolvedTarget;
      } else {
        warnings.push(`Step "${step.name}": Action "${action}" references unknown target "${target}"`);
        // Keep the original target for manual correction later
        step.actionRouting[action] = target;
      }
    });
  });
  
  // Count how many steps were auto-saved
  const autoSavedCount = processedSteps.filter(s => s.saved).length;
  
  return {
    workflow: {
      name: importedWorkflow.name,
      description: importedWorkflow.description || '',
      steps: processedSteps
    },
    warnings,
    autoSavedCount,
    totalSteps: processedSteps.length
  };
};

/**
 * Validates workflow JSON structure before processing
 * @param {string} jsonString - Raw JSON string
 * @returns {Object} - Parsed and validated workflow object
 */
export const validateWorkflowJson = (jsonString) => {
  if (!jsonString.trim()) {
    throw new Error('Please enter workflow JSON to import');
  }
  
  let parsedWorkflow;
  try {
    parsedWorkflow = JSON.parse(jsonString);
  } catch (error) {
    throw new Error(`Invalid JSON format: ${error.message}`);
  }
  
  // Basic structure validation
  if (typeof parsedWorkflow !== 'object' || parsedWorkflow === null) {
    throw new Error('Workflow must be a valid JSON object');
  }
  
  if (!parsedWorkflow.name || typeof parsedWorkflow.name !== 'string') {
    throw new Error('Workflow must have a valid name');
  }
  
  if (!parsedWorkflow.steps || !Array.isArray(parsedWorkflow.steps)) {
    throw new Error('Workflow must have a steps array');
  }
  
  if (parsedWorkflow.steps.length === 0) {
    throw new Error('Workflow must have at least one step');
  }
  
  return parsedWorkflow;
};

/**
 * Processes individual step JSON import for existing workflow
 * @param {Object} step - Current step object
 * @param {string} jsonString - JSON configuration to import
 * @param {Array} allSteps - All steps in workflow for reference resolution
 * @returns {Object} - Updated step configuration
 */
export const processStepJsonImport = (step, jsonString, allSteps = []) => {
  if (!jsonString.trim()) {
    throw new Error('Please enter step JSON to import');
  }
  
  let importedStep;
  try {
    importedStep = JSON.parse(jsonString);
  } catch (error) {
    throw new Error(`Invalid JSON format: ${error.message}`);
  }
  
  if (typeof importedStep !== 'object' || importedStep === null) {
    throw new Error('Step configuration must be a valid JSON object');
  }
  
  // Create step ID mapping from all steps for action routing resolution
  const { stepIdMap, stepNameMap } = createStepIdMapping(allSteps);
  
  // Merge imported configuration with current step
  const updates = {};
  
  if (importedStep.name) {
    updates.name = importedStep.name;
  }
  
  if (importedStep.type && VALID_STEP_TYPES.includes(importedStep.type)) {
    updates.type = importedStep.type;
  }
  
  if (importedStep.config) {
    updates.config = { ...step.config, ...importedStep.config };
  }
  
  // Process action routing
  if (importedStep.actionRouting || importedStep.on) {
    const actionRouting = importedStep.actionRouting || importedStep.on || {};
    const resolvedRouting = {};
    
    Object.entries(actionRouting).forEach(([action, target]) => {
      const resolvedTarget = resolveActionTarget(target, stepIdMap, stepNameMap);
      resolvedRouting[action] = resolvedTarget || target;
    });
    
    updates.actionRouting = resolvedRouting;
  }
  
  return updates;
};

/**
 * Generates import summary message for user feedback
 * @param {Object} importResult - Result from processWorkflowImport
 * @returns {Object} - Summary with message and type
 */
export const generateImportSummary = (importResult) => {
  const { autoSavedCount, totalSteps, warnings } = importResult;
  
  if (autoSavedCount === totalSteps && warnings.length === 0) {
    return {
      type: 'success',
      message: `All ${autoSavedCount} steps validated! Ready to save workflow.`,
      icon: '✅'
    };
  }
  
  if (autoSavedCount > 0 && warnings.length === 0) {
    return {
      type: 'warning',
      message: `${autoSavedCount} of ${totalSteps} steps auto-validated. Please complete remaining steps.`,
      icon: '⚠️'
    };
  }
  
  if (warnings.length > 0) {
    const warningText = warnings.length === 1 ? '1 connection issue' : `${warnings.length} connection issues`;
    return {
      type: 'warning',
      message: `Workflow imported with ${warningText}. Check action routing connections.`,
      icon: '⚠️',
      details: warnings
    };
  }
  
  return {
    type: 'info',
    message: 'Workflow imported. Please configure and save each step.',
    icon: 'ℹ️'
  };
};